/**
 * Shared layout constants for `EntityDateblock`.
 *
 * These values define the geometric rhythm of the dateblock (circle / line /
 * rect widths, milestone column width, etc.) and are consumed both by the
 * dateblock itself (`./index.tsx`) and by any wrapper that needs to reason
 * about its rendered width without measuring the DOM (e.g.
 * `./entity-dateblock-new-interface.tsx` aligning the Successor column).
 *
 * IMPORTANT: keep these as the single source of truth. The dateblock's
 * internal child components (`one-timeline-durations`, `two-timeline-icons`,
 * `three-timeline-milestones`) receive these values via the `widthCal` prop
 * so they remain in sync automatically.
 */

export const BLOCK_MARGIN = 25;
export const MILESTONES_WIDTH = 152;
export const TIMELINE_WIDTH = MILESTONES_WIDTH + BLOCK_MARGIN; // 177

/** Padding-left applied to the icon timeline row (`TimelineIconsOnly`). */
export const LEFT_PADDING_ICON_TIMELINE = 65;

/** Width of the final connector line before the float / deadline rect. */
export const LAST_LINE_WIDTH = 160;

/**
 * Shorter connector used only on material detail pages in Focused View so the
 * float / deadline panel can sit closer to the trailing milestone card.
 */
export const MATERIAL_FOCUSED_LAST_LINE_WIDTH = 66;

/** Tailwind `-left-20` legacy popover offset (80px left of the float rect). */
export const LEGACY_FLOAT_DEADLINE_POPOVER_LEFT_PX = -80;

export function getLastLineWidth(materialFocusedLayout = false): number {
  return materialFocusedLayout
    ? MATERIAL_FOCUSED_LAST_LINE_WIDTH
    : LAST_LINE_WIDTH;
}

export function getFloatBlockWidth(materialFocusedLayout = false): number {
  return getLastLineWidth(materialFocusedLayout) + RECT_WIDTH;
}

/** Geometry of the bl-circle / bl-rect / bl-line atoms in style.scss. */
export const CIRCLE_WIDTH = 24;
export const RECT_WIDTH = 48;

/** Half-segment line that bridges circle ↔ rect on either side of a milestone. */
export const LINE_WIDTH = (TIMELINE_WIDTH - CIRCLE_WIDTH - RECT_WIDTH) / 2; // 52.5

/** Horizontal offset of the first circle from the column's left edge. */
export const LEFT_MARGIN = (MILESTONES_WIDTH - CIRCLE_WIDTH) / 2; // 64

/** `px-5` applied to the dateblock outer container. */
export const DATEBLOCK_HORIZONTAL_PADDING = 20;

/** Fixed width of predecessor / successor link cards. */
export const LINK_CARD_WIDTH = 240;

/**
 * Horizontal inset of link cards (and group header text) from the gray
 * accordion container edge.
 */
export const LINK_CARD_HORIZONTAL_MARGIN_PX = 8;

/** Vertical padding on the gray accordion group header row. */
export const LINKS_GROUP_HEADER_VERTICAL_PADDING_PX = 8;

/** Left padding on gray accordion group titles (e.g. Submittals, Materials). */
export const LINKS_GROUP_TITLE_LEFT_PADDING_PX = 12;

/** Horizontal padding inside each link card section. */
export const LINK_CARD_HORIZONTAL_PADDING_PX = 8;

/** Fixed height of predecessor / successor link cards. */
export const LINK_CARD_HEIGHT_PX = 32;

/** Tailwind `pl-3` / `pr-3` on the outer edge of each links column. */
export const LINKS_COLUMN_OUTER_PADDING_PX = 12;

/** Horizontal gap between the dateblock edge and the links-column cross line. */
export const SUCCESSOR_CONNECTOR_LINE_LENGTH_PX = 14;

/**
 * Gap between the predecessor vertical cross line and the left edge of the
 * first milestone card (e.g. "Material Released").
 */
export const LINKS_COLUMN_CROSS_TO_MILESTONE_CARD_GAP_PX = 12;

/**
 * Inner gutter between predecessor link cards and the dateblock. Kept equal to
 * {@link SUCCESSOR_CONNECTOR_LINE_LENGTH_PX} so the highlighted side gaps match.
 */
export const LINKS_COLUMN_INNER_GUTTER_PX = SUCCESSOR_CONNECTOR_LINE_LENGTH_PX;

/**
 * Width of the successor header axis region (`w-24`). Card content starts
 * here so cards left-align with the "Successors" label text.
 */
export const LINKS_COLUMN_SUCCESSOR_AXIS_WIDTH_PX = 96;

/** Extra axis header width for material successors on submittal focal entities. */
export const SUBMITTAL_SUCCESSOR_AXIS_EXTRA_WIDTH_PX = 40;

/**
 * Pulls submittal successor cards 8px closer to the vertical cross line
 * (tighter gap between the axis and the Materials accordion).
 */
export const SUBMITTAL_SUCCESSOR_CARD_LEFT_PADDING_REDUCTION_PX = 18;

/** Successor axis region width when the focal entity is a submittal. */
export const SUBMITTAL_LINKS_COLUMN_SUCCESSOR_AXIS_WIDTH_PX =
  LINKS_COLUMN_SUCCESSOR_AXIS_WIDTH_PX +
  SUBMITTAL_SUCCESSOR_AXIS_EXTRA_WIDTH_PX;

/** Card band inside column padding (card width + gray-box horizontal inset). */
export const LINKS_COLUMN_CARD_CONTENT_WIDTH =
  LINK_CARD_WIDTH + LINK_CARD_HORIZONTAL_MARGIN_PX * 2;

/** Fixed width of the predecessor links column. */
export const LINKS_COLUMN_PREDECESSOR_WIDTH =
  LINKS_COLUMN_OUTER_PADDING_PX +
  LINKS_COLUMN_CARD_CONTENT_WIDTH +
  LINKS_COLUMN_INNER_GUTTER_PX;

/**
 * Horizontal bridge from the predecessor axis line into the dateblock: spans
 * the milestone-card gap plus the inset to the first timeline circle.
 */
export const LINKS_COLUMN_AXIS_BRIDGE_INTO_DATEBLOCK_PX =
  LEFT_MARGIN + LINKS_COLUMN_CROSS_TO_MILESTONE_CARD_GAP_PX;

/**
 * Pulls the dateblock left so the gap from the vertical cross to the first
 * milestone card equals {@link LINKS_COLUMN_CROSS_TO_MILESTONE_CARD_GAP_PX}.
 */
export function computePredecessorDateblockMarginLeftPx(): number {
  return (
    LINKS_COLUMN_CROSS_TO_MILESTONE_CARD_GAP_PX - DATEBLOCK_HORIZONTAL_PADDING
  );
}

/**
 * Computes the total successor links-column width for a given axis region width.
 * Single source of truth for every successor column width below.
 */
export function computeLinksColumnSuccessorWidth(
  axisWidthPx: number = LINKS_COLUMN_SUCCESSOR_AXIS_WIDTH_PX
): number {
  return (
    axisWidthPx +
    LINKS_COLUMN_CARD_CONTENT_WIDTH +
    LINKS_COLUMN_OUTER_PADDING_PX
  );
}

/**
 * Successor column is wider so cards can start under the label while keeping
 * the same pixel width as predecessors.
 */
export const LINKS_COLUMN_SUCCESSOR_WIDTH = computeLinksColumnSuccessorWidth();

/** Total width of the submittal focal-entity successor links column. */
export const SUBMITTAL_LINKS_COLUMN_SUCCESSOR_WIDTH =
  computeLinksColumnSuccessorWidth(
    SUBMITTAL_LINKS_COLUMN_SUCCESSOR_AXIS_WIDTH_PX
  );

/** Fixed width of the Bid Package Date planner successor column. */
export const BID_PACKAGE_SUCCESSORS_COLUMN_WIDTH = 320;

/** Gap between the dateblock float / deadline block and the Date planner panel. */
export const BID_PACKAGE_SUCCESSORS_GAP_PX = 24;

/** Horizontal gap between milestone cards (`space-x-6` on the cards row). */
export const MILESTONE_CARD_ROW_GAP_PX = 24;

/** Tailwind `w-52` width of the float / deadline popover panel. */
export const FLOAT_DEADLINE_POPOVER_WIDTH_PX = 208;

/** Gap between the trailing milestone card and the float / deadline popover. */
export const FLOAT_DEADLINE_MILESTONE_CARD_GAP_PX = 12;

/**
 * Left offset of the float/deadline popover inside the trailing `bl-rect`.
 * Aligns the panel's left edge {@link FLOAT_DEADLINE_MILESTONE_CARD_GAP_PX}
 * past the trailing milestone card's right edge by reconciling icon-row
 * geometry with the cards row (`space-x-6` + {@link MILESTONES_WIDTH}).
 */
export function computeFloatDeadlinePopoverLeftPx(
  milestoneCount: number
): number {
  if (milestoneCount <= 0) {
    return 0;
  }

  const lastIndex = milestoneCount - 1;
  const trailingMilestoneCardRight =
    lastIndex * (MILESTONES_WIDTH + MILESTONE_CARD_ROW_GAP_PX) +
    MILESTONES_WIDTH;
  const floatRectLeft =
    LEFT_MARGIN +
    lastIndex * TIMELINE_WIDTH +
    CIRCLE_WIDTH +
    MATERIAL_FOCUSED_LAST_LINE_WIDTH;

  return (
    trailingMilestoneCardRight -
    floatRectLeft +
    FLOAT_DEADLINE_MILESTONE_CARD_GAP_PX
  );
}

/** How far the float/deadline popover extends past the geometric `bl-rect` right edge. */
export function computeFloatDeadlinePopoverRightOverflowPx(
  milestoneCount: number
): number {
  return (
    computeFloatDeadlinePopoverLeftPx(milestoneCount) +
    FLOAT_DEADLINE_POPOVER_WIDTH_PX -
    RECT_WIDTH
  );
}

/**
 * Visual float/deadline extent past the geometric rect, consumed by bid-package
 * successor margin math (classic `-left-20` popover positioning).
 */
export const BID_PACKAGE_FLOAT_DEADLINE_OVERFLOW_PX = 80;

/** Extra left pull on the successor column so the cross sits closer to deadline. */
export const SUCCESSOR_CROSS_ADDITIONAL_SHIFT_PX = 20;

/** Right inset of the successor horizontal axis before the column title. */
export const LINKS_COLUMN_SUCCESSOR_AXIS_LINE_RIGHT_INSET_PX = 20;

/** Gap between the vertical cross and the horizontal axis line's right end. */
export const LINKS_COLUMN_SUCCESSOR_CROSS_GAP_FROM_AXIS_LINE_END_PX = 12;

/**
 * Extra right offset for the successor vertical cross on material / design
 * package detail Focused View so the junction sits further from the dateblock.
 */
export const MATERIAL_FOCUSED_SUCCESSOR_CROSS_RIGHT_SHIFT_PX = 20;

export type FocusedSuccessorLayoutOptions = {
  materialFocusedLayout?: boolean;
  designPackageFocusedLayout?: boolean;
};

function usesFocusedSuccessorCrossShift(
  options?: FocusedSuccessorLayoutOptions
): boolean {
  return !!(
    options?.materialFocusedLayout || options?.designPackageFocusedLayout
  );
}

/**
 * Left offset of the successor vertical cross: sits
 * {@link LINKS_COLUMN_SUCCESSOR_CROSS_GAP_FROM_AXIS_LINE_END_PX} before the
 * horizontal axis line's right end.
 */
export function computeSuccessorCrossLeftPx(
  axisWidthPx: number = LINKS_COLUMN_SUCCESSOR_AXIS_WIDTH_PX,
  options?: FocusedSuccessorLayoutOptions
): number {
  const base =
    axisWidthPx -
    LINKS_COLUMN_SUCCESSOR_AXIS_LINE_RIGHT_INSET_PX -
    LINKS_COLUMN_SUCCESSOR_CROSS_GAP_FROM_AXIS_LINE_END_PX;

  return (
    base +
    (usesFocusedSuccessorCrossShift(options)
      ? MATERIAL_FOCUSED_SUCCESSOR_CROSS_RIGHT_SHIFT_PX
      : 0)
  );
}

/**
 * Right inset of the successor horizontal axis so its end stays
 * {@link LINKS_COLUMN_SUCCESSOR_CROSS_GAP_FROM_AXIS_LINE_END_PX} past the
 * vertical cross (extends the line when the cross is shifted right).
 */
export function computeSuccessorAxisLineRightInsetPx(
  axisWidthPx: number = LINKS_COLUMN_SUCCESSOR_AXIS_WIDTH_PX,
  options?: FocusedSuccessorLayoutOptions
): number {
  const crossLeftPx = computeSuccessorCrossLeftPx(axisWidthPx, options);
  return (
    axisWidthPx -
    crossLeftPx -
    LINKS_COLUMN_SUCCESSOR_CROSS_GAP_FROM_AXIS_LINE_END_PX
  );
}

/**
 * Left edge of the successor horizontal axis segment: aligns with the dateblock
 * content end so the connector fills the gap to the cross without overlapping
 * the last offset line.
 */
export const LINKS_COLUMN_SUCCESSOR_AXIS_LINE_LEFT_PX =
  SUCCESSOR_CROSS_ADDITIONAL_SHIFT_PX - SUCCESSOR_CONNECTOR_LINE_LENGTH_PX;

/**
 * Left margin for the Bid Package Date planner column: pulls into trailing
 * slack while leaving a fixed gap after the visual float / deadline popover.
 */
export function computeBidPackageSuccessorMarginLeftPx(
  successorShiftPx: number
): number {
  if (successorShiftPx <= 0) {
    return BID_PACKAGE_SUCCESSORS_GAP_PX;
  }

  const trailingSlack =
    successorShiftPx +
    SUCCESSOR_CONNECTOR_LINE_LENGTH_PX -
    SUCCESSOR_CROSS_ADDITIONAL_SHIFT_PX;

  return (
    -trailingSlack +
    BID_PACKAGE_FLOAT_DEADLINE_OVERFLOW_PX +
    BID_PACKAGE_SUCCESSORS_GAP_PX
  );
}

/**
 * Width offset subtracted from `totalDateBlockWidth` when sizing the outer
 * container in `index.tsx` (i.e. `style={{ width: totalDateBlockWidth - 110 }}`).
 * Keep both call sites referencing this constant.
 */
export const DATEBLOCK_WIDTH_OFFSET = 110;

/**
 * Compensation applied to `totalDateBlockWidth` when NOT every milestone
 * carries an offset rect: shrinks the dateblock so the trailing slack stays
 * visually balanced. See `computeTotalDateBlockWidth` for context.
 */
export const NON_TRAILING_OFFSET_COMPENSATION = -95;

/**
 * Trailing multiplier applied to milestone count for the dateblock's total
 * width. Empirically derived to leave room for the float / deadline column
 * after the last milestone.
 */
export const TIMELINE_TRAILING_MULTIPLIER = 1.7;

export type DateblockMilestoneLike = {
  feature_workflow_instance_offsets_as_src?: ReadonlyArray<unknown> | null;
};

/**
 * Returns true when every supplied milestone owns at least one offset entry.
 * Mirrors the `isLastOffset` flag in `index.tsx`.
 */
export function allMilestonesHaveOffsets(
  milestones: ReadonlyArray<DateblockMilestoneLike>,
  options?: { assumeLastHasOffset?: boolean }
): boolean {
  if (milestones.length === 0) return false;
  const lastIndex = milestones.length - 1;
  return milestones.every((m, i) => {
    if (options?.assumeLastHasOffset && i === lastIndex) return true;
    return (m?.feature_workflow_instance_offsets_as_src?.length ?? 0) > 0;
  });
}

/**
 * Replicates the `totalDateBlockWidth` formula from `EntityDateblock`. Kept
 * here so multiple sites can derive the dateblock's outer width without
 * duplicating the arithmetic.
 */
export function computeTotalDateBlockWidth(
  milestones: ReadonlyArray<DateblockMilestoneLike>,
  options?: { materialFocusedLayout?: boolean }
): number {
  /**
   * Material Focused View always renders a trailing segment after the last
   * milestone (a plain line when the real offset / On-site Storage is absent),
   * so keep the width identical to the offset-present case instead of shrinking.
   */
  const lastOffsetWidth = allMilestonesHaveOffsets(milestones, {
    assumeLastHasOffset: options?.materialFocusedLayout
  })
    ? 0
    : NON_TRAILING_OFFSET_COMPENSATION;
  return (
    (milestones.length + TIMELINE_TRAILING_MULTIPLIER) * TIMELINE_WIDTH +
    getLastLineWidth(options?.materialFocusedLayout) +
    lastOffsetWidth
  );
}

/**
 * Width of the dateblock's outer container as rendered in the DOM. Equals
 * `computeTotalDateBlockWidth - DATEBLOCK_WIDTH_OFFSET`.
 */
export function computeDateblockOuterWidth(
  milestones: ReadonlyArray<DateblockMilestoneLike>,
  options?: { materialFocusedLayout?: boolean }
): number {
  return (
    computeTotalDateBlockWidth(milestones, options) - DATEBLOCK_WIDTH_OFFSET
  );
}

export type SuccessorShiftMilestone = DateblockMilestoneLike & {
  sequence_no?: number;
};

export type SuccessorShiftOptions = {
  showFloat?: boolean;
  desiredLineLenPx?: number;
  materialFocusedLayout?: boolean;
};

/**
 * Computes the empty horizontal trailing space inside `EntityDateblock`
 * between the rightmost visible element (last offset / float rect) and the
 * dateblock div's right edge.
 *
 * Used to shift the successor column LEFT so the cross sits one short
 * connector-line away from the trailing rect.
 */
export function computeSuccessorShiftPx(
  rawMilestones: ReadonlyArray<SuccessorShiftMilestone> | undefined,
  options?: SuccessorShiftOptions
): number {
  const {
    showFloat = true,
    desiredLineLenPx = SUCCESSOR_CONNECTOR_LINE_LENGTH_PX,
    materialFocusedLayout = false
  } = options ?? {};
  const milestones = (rawMilestones ?? [])
    .slice()
    .sort((a, b) => (a?.sequence_no ?? 0) - (b?.sequence_no ?? 0));
  if (milestones.length === 0) return 0;

  const divWidth = computeDateblockOuterWidth(milestones, {
    materialFocusedLayout
  });

  let contentWidth = DATEBLOCK_HORIZONTAL_PADDING + LEFT_MARGIN;
  const floatBlockWidth = getFloatBlockWidth(materialFocusedLayout);
  milestones.forEach((m, i) => {
    const isLast = i === milestones.length - 1;
    const hasOffset =
      (m?.feature_workflow_instance_offsets_as_src?.length ?? 0) > 0;
    contentWidth += CIRCLE_WIDTH;
    if (isLast && showFloat) contentWidth += floatBlockWidth;
    /**
     * The last milestone on a Material Focused View always renders a trailing
     * segment — a rect when a real offset exists, otherwise a plain line of the
     * same footprint — so count it either way to keep the successor position
     * identical whether or not On-site Storage is present.
     */
    const rendersTrailingFootprint =
      hasOffset || (isLast && materialFocusedLayout);
    if (rendersTrailingFootprint) contentWidth += LINE_WIDTH + RECT_WIDTH;
    if (!isLast) contentWidth += LINE_WIDTH;
  });

  const trailingSlack = Math.max(0, divWidth - contentWidth);
  return (
    Math.max(0, trailingSlack - desiredLineLenPx) +
    SUCCESSOR_CROSS_ADDITIONAL_SHIFT_PX
  );
}
